// @spec SHL-STR-017
// @spec SHL-STR-019
'use client';

import { Command } from 'cmdk';
import { Building2, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SearchResultItem } from './SearchResultItem';

import type { SearchCategory, SearchResult } from '@/api/search';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Selection context passed to onSelect and onSelectNewTab handlers.
 */
export interface SearchResultSelection {
  /** The selected result */
  result: SearchResult;
  /** Category name of the result */
  category: string;
  /** 0-indexed position within all results */
  position: number;
}

export interface SearchResultsProps {
  /** Search results grouped by category */
  results: SearchCategory[];
  /** Whether results are loading */
  isLoading: boolean;
  /** Search query string (for highlighting) */
  query: string;
  /** Called when a result is selected */
  onSelect: (selection: SearchResultSelection) => void;
  /** Called when a result should open in new tab */
  onSelectNewTab?: (selection: SearchResultSelection) => void;
}

/**
 * Get the appropriate Lucide icon component based on category icon name.
 */
function getCategoryIcon(iconName: string) {
  switch (iconName) {
    case 'users':
      return Users;
    case 'building-2':
      return Building2;
    default:
      return Users;
  }
}

/**
 * Loading skeleton for search results.
 */
function SearchResultsSkeleton() {
  return (
    <div className="space-y-4 p-2" aria-busy="true" aria-label={t('aria.loadingSearchResults')}>
      {/* Category skeleton */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Empty state when no results found.
 */
function SearchResultsEmpty({ query }: { query: string }) {
  const { t } = useTranslation('common');
  return (
    <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
      {t('search.noResultsFor', { query })}
    </Command.Empty>
  );
}

/**
 * Search results display component.
 *
 * Renders search results grouped by category with:
 * - Loading skeleton during fetch
 * - Empty state when no results
 * - Category groups with icons and counts
 * - Individual result items with highlighting
 */
export function SearchResults({
  results,
  isLoading,
  query,
  onSelect,
  onSelectNewTab,
}: SearchResultsProps) {
  if (isLoading) {
    return <SearchResultsSkeleton />;
  }

  const hasResults = results.length > 0 && results.some((cat) => cat.results.length > 0);

  if (!hasResults) {
    return <SearchResultsEmpty query={query} />;
  }

  // Calculate cumulative position for analytics tracking
  let cumulativePosition = 0;

  return (
    <>
      {results.map((category) => {
        if (category.results.length === 0) return null;

        const CategoryIcon = getCategoryIcon(category.icon);
        const categoryStartPosition = cumulativePosition;

        // Update cumulative for next category
        cumulativePosition += category.results.length;

        return (
          <Command.Group
            key={category.name}
            heading={
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <CategoryIcon className="h-3.5 w-3.5" />
                <span>{category.displayName}</span>
                <span className="text-xs font-normal">({category.count})</span>
              </div>
            }
            className={cn('px-2 py-1')}
          >
            {category.results.map((result, index) => {
              const position = categoryStartPosition + index;
              const selection: SearchResultSelection = {
                result,
                category: category.name,
                position,
              };

              return (
                <SearchResultItem
                  key={result.id}
                  result={result}
                  query={query}
                  onSelect={() => {
                    onSelect(selection);
                  }}
                  {...(onSelectNewTab
                    ? {
                        onSelectNewTab: () => {
                          onSelectNewTab(selection);
                        },
                      }
                    : {})}
                />
              );
            })}
          </Command.Group>
        );
      })}
    </>
  );
}
